import React from 'react';
import { motion } from 'framer-motion';
import { GiftIcon } from 'lucide-react';
const bonuses = [{
  id: '01',
  title: 'Hip Hop Cueing, Corrections & Teaching Scripts Library',
  value: '12',
  desc: 'A complete library of teaching cues, verbal scripts, and correction strategies organized by movement category: posture and alignment, bounce and rock, grooves, isolations, body rolls, weight shifts, footwork, arms, coordination, directions, levels, rhythm, musicality, freestyle, and choreography. Includes prepared scripts so the instructor can explain what to do, how to do it, and what the student should feel during every movement. Group corrections, individual feedback, confidence-building phrases, and opening and closing scripts for every class.',
  benefit: 'No preparation needed. Open it, read it, teach.'
}, {
  id: '02',
  title: 'Mixed-Level Hip Hop Progression & Modification System',
  value: '12',
  desc: 'Complete tools to adapt every class to students with different levels of experience — from absolute beginners to advanced dancers. Every skill includes basic, intermediate, and advanced versions, with modifications for footwork complexity, speed, impact level, arm variations, and directional changes. Includes strategies to keep advanced students challenged, tools to support insecure students, partner and group organization systems, and weekly and monthly progression templates.',
  benefit: 'Three versions of every skill. Ready before the class starts.'
}, {
  id: '03',
  title: 'Hip Hop Warm-Up, Conditioning & Cool-Down Vault',
  value: '12',
  desc: 'A complete collection of warm-ups, conditioning exercises, and cool-down sequences organized by purpose and level. Includes general activation, neck and shoulder mobility, spinal mobility, hip activation, knee and ankle preparation, progressive bounce, cardiovascular groove, coordination warm-ups, and musicality activation. Conditioning covers leg stability, core control, glute strength, arm and shoulder endurance, balance, coordination, level changes, and cardiovascular resistance — all without equipment, with low-impact options and level progressions. Cool-down sequences of 5 and 10 minutes included.',
  benefit: 'Open it, choose the routine, and your class starts strong.'
}, {
  id: '04',
  title: 'Hip Hop Student Engagement, Cypher & Freestyle Activity Bank',
  value: '12',
  desc: 'A complete bank of activities to keep students participative, creative, and genuinely connected to every class. Includes follow the leader, call and response, copy and transform, freestyle by groove, direction, level, arms, footwork, pauses, and emotion — plus partner exchanges, small group creation, friendly battles without elimination, beginner cyphers, team cyphers, collective combination building, and choreography memory challenges. Includes cypher respect guidelines, strategies to prevent pressure or embarrassment, and ways to celebrate individual progress.',
  benefit: 'Never teach the same class twice. The variety is already built in.'
}, {
  id: '05',
  title: 'Emergency & Substitute Hip Hop Class System',
  value: '10',
  desc: '30 complete, ready-to-teach hip hop classes for emergencies, substitutions, or any day when you need a session prepared immediately. Every emergency class includes objective, duration, warm-up, technical activity, step-by-step instructions, combination or creative activity, level adaptations, cool-down, and options to extend the session. Classes include Groove Foundations Express, Running Man Workshop, Freestyle for Beginners, Cypher Confidence Class, Hip Hop Conditioning, Low-Impact Hip Hop, Performance Skills Class, Complete Hip Hop Experience, and 22 more.',
  benefit: 'Open it, choose the class, walk in prepared.'
}];
export function BonusSection() {
  return <section className="py-24 px-4 bg-cream" data-id="element-20" data-mp="2z0s7gbxia01g">
      <div className="max-w-5xl mx-auto" data-id="element-21" data-mp="2z2lf9ujuuqb1">
        <div className="text-center mb-12" data-id="element-22" data-mp="3f86gf5t99pjj">
          <p className="text-burgundy font-bold tracking-widest uppercase mb-2" data-id="element-23" data-mp="3f8yd6jyl9tbe">
            Included at no extra cost
          </p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold uppercase text-ink" data-id="element-24" data-mp="3afdbohxseh7g">
            🎁 Component 5 — The 5 Strategic Bonuses
          </h2>
          <p className="text-ink-soft mt-4 max-w-2xl mx-auto" data-id="element-25" data-mp="30kbnbobc5j5x">
            These 5 bonuses are not extras thrown in to inflate the price. They
            are the support system that makes the other 4 components work better
            — every single class, every single week.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-26" data-mp="3o7tf3juloh8w">
          {bonuses.map((bonus, index) => <motion.article key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`bg-cream-light border border-gold/60 rounded-2xl p-8 relative hover:border-burgundy/60 transition-colors shadow-sm ${index === 4 ? 'md:col-span-2' : ''}`} data-id="element-27" data-mp="3j8qf4dth917s" data-mpi={index}>
              <div className="absolute top-0 right-0 bg-burgundy text-cream-light px-4 py-2 rounded-bl-2xl rounded-tr-2xl font-bold text-sm" data-id="element-28" data-mp="xb06db0gi3m9">
                BONUS {bonus.id}
              </div>

              <div className="w-12 h-12 bg-cream border border-gold rounded-full flex items-center justify-center text-burgundy mb-6" data-id="element-29" data-mp="170th69sryepg">
                <GiftIcon size={24} data-id="element-30" data-mp="1b0tvlufvipwi" />
              </div>

              <h3 className="text-xl font-serif font-bold uppercase mb-2 pr-24 text-ink" data-id="element-31" data-mp="1g182va9nhvth">
                {bonus.title}
              </h3>

              <div className="inline-block bg-cream border border-line px-3 py-1 rounded mb-4" data-id="element-32" data-mp="1kyzwc8py1pzs">
                <span className="text-ink-muted text-sm" data-id="element-33" data-mp="3htmho0gwek5o">
                  Real value: <span className="line-through" data-id="element-34" data-mp="3mr5xv5x5p709">${bonus.value}</span>
                </span>
                <span className="text-burgundy font-bold ml-2" data-id="element-35" data-mp="3eqq6erp9p0dr">→ FREE</span>
              </div>

              <p className="text-ink-soft text-sm mb-6 leading-relaxed" data-id="element-36" data-mp="3apo585bsie95">
                {bonus.desc}
              </p>

              <p className="text-burgundy text-sm italic font-semibold" data-id="element-37" data-mp="3opfpuowtasw4">
                {bonus.benefit}
              </p>
            </motion.article>)}
        </div>
      </div>
    </section>;
}